import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, API_V1_PATH, AUTH_FRONT_ORIGIN } from '../config/env';

const client: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Mandatory for Cookie-based SSO
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request Interceptor: Add Authorization header unless it's login/reissue
client.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const isAuthRequest = config.url?.includes('/auth/login') || config.url?.includes('/auth/reissue');
    
    const token = localStorage.getItem('accessToken');
    if (token && config.headers && !isAuthRequest) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor: Handle 401 and Redirect to SSO
client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest?.url || '';
    const path = window.location.pathname;
    
    // Don't intercept 401s on auth endpoints to prevent loops
    const isAuthEndpointRequest =
      url.includes('/auth/me') ||
      url.includes('/auth/login') ||
      url.includes('/auth/signup') ||
      url.includes('/auth/reissue') ||
      url.includes('/auth/logout') ||
      url.includes('/auth/password-reset/');
    const isPublicAuthPath =
      path.includes('/login') ||
      path.includes('/signup') ||
      path.includes('/register') ||
      path.includes('/reset-password');
    const isAdminDataRequest =
      url.includes('/api/v1/admin/services') ||
      url.includes('/api/v1/admin/users');
    const suppressLoginRedirect = (path.startsWith('/services') || path.startsWith('/users')) && isAdminDataRequest;

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpointRequest) {
      originalRequest._retry = true;
      
      // Try reissue via cookie (no body needed per spec)
      try {
        const res = await axios.post(`${API_BASE_URL}${API_V1_PATH}/auth/reissue`, {}, { withCredentials: true });
        if (res.status === 200) {
          if (res.data?.result?.accessToken) {
            localStorage.setItem('accessToken', res.data.result.accessToken);
          }
          return client(originalRequest);
        }
      } catch (reissueError) {
        // Reissue failed, redirect only when not already on auth pages and not on admin data pages.
        if (!isPublicAuthPath && !suppressLoginRedirect) {
          const redirectUri = encodeURIComponent(window.location.href);
          window.location.href = `${AUTH_FRONT_ORIGIN}/login?redirect_uri=${redirectUri}`;
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export default client;
